import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  Drawing,
  DrawingAttachment,
  DrawingPayload,
} from '../types/drawing';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchDrawings(
  keyword?: string,
  contractId?: number | null
): Promise<Drawing[]> {
  const params: Record<string, string | number> = {};
  if (keyword?.trim()) params.q = keyword.trim();
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.get<Drawing[]>('/drawings', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function fetchNextDrawingNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/drawings/suggestions/drawing-no'
  );
  return data;
}

export async function fetchNextDrawingProductCode(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/drawings/suggestions/product-code'
  );
  return data;
}

export async function fetchDrawing(drawingId: number): Promise<Drawing> {
  const { data } = await apiClient.get<Drawing>(`/drawings/${drawingId}`);
  return data;
}

export async function createDrawing(payload: DrawingPayload): Promise<Drawing> {
  const { data } = await apiClient.post<Drawing>('/drawings', payload);
  return data;
}

export async function updateDrawing(
  drawingId: number,
  payload: DrawingPayload
): Promise<Drawing> {
  const { data } = await apiClient.patch<Drawing>(
    `/drawings/${drawingId}`,
    payload
  );
  return data;
}

export async function deleteDrawing(drawingId: number): Promise<void> {
  await apiClient.delete(`/drawings/${drawingId}`);
}

export async function approveDrawing(drawingId: number): Promise<Drawing> {
  const { data } = await apiClient.post<Drawing>(`/drawings/${drawingId}/approve`);
  return data;
}

export async function revokeDrawing(drawingId: number): Promise<Drawing> {
  const { data } = await apiClient.post<Drawing>(`/drawings/${drawingId}/revoke`);
  return data;
}

export async function uploadDrawingAttachments(
  drawingId: number,
  files: File[]
): Promise<DrawingAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<DrawingAttachment[]>(
    `/drawings/${drawingId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteDrawingAttachment(
  drawingId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/drawings/${drawingId}/attachments/${attachmentId}`);
}

export async function downloadDrawingAttachment(
  drawingId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/drawings/${drawingId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}


export async function downloadDrawingPdf(
  drawingId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/drawings/${drawingId}/pdf`,
    fileName || `도면_${drawingId}.pdf`
  );
}
